import MCPLogo from "@/assets/MCPLogo.svg";
import { Button } from "@/components/ui";
import { useHandler } from "@/hooks";
import { useAutoResizeTextarea, useInputFocus } from "@/hooks";
import { useActiveChatId } from "@/lib/hooks/useActiveChatId";
import { useCancellableMessages } from "@/lib/hooks/useCancellableMessages";
import { useIsMessageGenerating } from "@/lib/hooks/useIsMessageGenerating";
import { useWebSocketChat } from "@/lib/hooks/useWebSocketChat";
import { cn } from "@/lib/utils";
import {
  useInputStore,
  useMessageStore,
  usePanelStore,
  useRefInputStore,
} from "@/state/stores";
import { ArrowUp, CheckSquare, Code2Icon, Edit3, X } from "lucide-react";
import { memo, useCallback, useEffect } from "react";
import styles from "./ChatInput.module.css";
import { Drawer } from "./Drawer";
import { usePlaceholderAnimation, useTextChangeVisual } from "./hooks";

const TEMPLATE_PROMPTS = [
  {
    id: "weather-mcp",
    label: "Weather MCP",
    prompt:
      "Generate an MCP (Model Context Protocol) server for weather data with the following features:\n\n- Get current weather for a specific location (mocked data)\n- Get weather forecast for a specific location (mocked data)\n- Get weather history for a specific location (mocked data)\n- Use realistic but fake weather data - no third-party API calls required",
  },
  {
    id: "ai-goose-jokes",
    label: "AI Jokes",
    prompt:
      "Create an AI-powered goose joke teller API with the following features:\n\n- OpenAI API integration for generating goose-themed jokes\n- Environment variable and secret configuration for OpenAI API key\n- RESTful endpoints:\n  - GET /jokes/random - Get a random goose joke\n  - GET /jokes/category/:type - Get jokes by category (silly, puns, dad-jokes)\n  - POST /jokes/custom - Generate custom goose jokes based on user prompts\n- Rate limiting to manage OpenAI API costs\n- Response formatting with joke setup, punchline, and goose rating (1-5 honks)",
  },
  {
    id: "personal-crm-mcp",
    label: "Personal Contacts MCP",
    prompt:
      "Generate an MCP (Model Context Protocol) server for a personal CRM system with the following features:\n- Contact management (create, update, delete contacts)\n- Company name and role. Contact search and filtering capabilities. Store contact information in a d1 database.",
  },
  {
    id: "blog",
    label: "Blog API",
    prompt:
      "Create a RESTful blog API with the following features:\n\n- User authentication and authorization\n- CRUD operations for blog posts\n  - Each post should have: title, content (Markdown), author, tags, and publish date\n  - Support for draft/published status\n- Comment system\n  - Nested comments support\n  - Moderation capabilities",
  },
] as const;

export const ChatInput = memo(function ChatInput({
  isNewChat,
  onSendMessage,
}: { isNewChat: boolean; onSendMessage: () => void }) {
  const setMessage = useInputStore((state) => state.setMessage);
  const { adjustHeight } = useAutoResizeTextarea();
  const focusInput = useInputFocus();
  const showMessageValue = useShowMessageValue();

  usePlaceholderAnimation(isNewChat);
  useTextChangeVisual(isNewChat && showMessageValue);

  const handleTemplateClick = (prompt: string) => {
    setMessage(prompt);
    setTimeout(() => {
      adjustHeight();
      focusInput();
    }, 0);
  };

  return (
    <div
      className={cn(
        "sticky bottom-0 left-0 mx-auto pt-5 bg-background/70 backdrop-blur-lg w-full",
        styles.chatInputContainer,
      )}
    >
      <div className="max-w-2xl mx-auto px-5 grid grid-cols-[minmax(0,1fr)] gap-2 pb-5">
        <Drawer />
        {isNewChat && showMessageValue ? (
          <>
            <Input placeholder="Build me a..." onSendMessage={onSendMessage} />
            <div className="flex gap-3 w-full">
              {TEMPLATE_PROMPTS.map((template) => (
                <Button
                  key={template.id}
                  variant="outline"
                  className="flex items-center gap-2 flex-1 border-dashed border-border rounded-lg hover:bg-secondary"
                  onClick={() => handleTemplateClick(template.prompt)}
                >
                  <Code2Icon className="h-4 w-4" /> {template.label}
                </Button>
              ))}
            </div>
          </>
        ) : (
          <>
            <Input onSendMessage={onSendMessage} />
          </>
        )}
      </div>
    </div>
  );
});

const DrawerTabs = memo(function DrawerTabs() {
  const { setActiveTab, activeTab, isPanelOpen } = usePanelStore();
  const chatId = useActiveChatId();

  return (
    <div className="flex gap-2">
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setActiveTab("suggestions")}
        disabled={!chatId}
        className={cn(
          "h-auto px-2 py-1 rounded-md hover:bg-neutral-700/80 text-muted-foreground hover:text-foreground flex items-center gap-1.5",
          activeTab === "suggestions" &&
            isPanelOpen &&
            "bg-neutral-700/80 text-foreground",
        )}
      >
        <Edit3 className="h-4 w-4" /> Suggestions
      </Button>

      <Button
        variant="ghost"
        size="sm"
        onClick={() => setActiveTab("mcp-servers")}
        disabled={!chatId}
        className={cn(
          "h-auto px-2 py-1 rounded-md hover:bg-neutral-700/80 text-muted-foreground hover:text-foreground flex items-center gap-1.5",
          activeTab === "mcp-servers" &&
            isPanelOpen &&
            "bg-neutral-700/80 text-foreground",
        )}
      >
        <MCPLogo className="h-3.5 w-3.5" />
        MCP Servers
      </Button>

      <Button
        variant="ghost"
        size="sm"
        onClick={() => setActiveTab("tasks")}
        disabled={!chatId}
        className={cn(
          "h-auto px-2 py-1 rounded-md hover:bg-neutral-700/80 text-muted-foreground hover:text-foreground flex items-center gap-1.5",
          activeTab === "tasks" &&
            isPanelOpen &&
            "bg-neutral-700/80 text-foreground",
        )}
      >
        <CheckSquare className="h-3.5 w-3.5" />
        Tasks
      </Button>
    </div>
  );
});

type InputProps = {
  placeholder?: string;
  onSendMessage: () => void;
};

export const Input = memo(function Input({
  placeholder = "Ask Fiberplane...",
  onSendMessage,
}: InputProps) {
  const isMessageGenerating = useIsMessageGenerating();
  const message = useInputStore((state) => state.message);
  const hasMessages = useMessageStore((state) => state.messages.length > 0);
  const setMessage = useInputStore((state) => state.setMessage);
  const cancellableMessageIds = useCancellableMessages();
  const textareaRef = useRefInputStore(({ inputRef }) => inputRef);
  const { adjustHeight, resetHeight } = useAutoResizeTextarea();
  const { sendMessage, createNewChat, cancelMessage } = useWebSocketChat();

  // Reset height when processing starts
  useEffect(() => {
    if (isMessageGenerating) {
      resetHeight();
    } else if (textareaRef.current) {
      // Focus the textarea when processing is done
      textareaRef.current.focus();
    }
  }, [isMessageGenerating, resetHeight, textareaRef]);

  const handleMessageChange = useHandler(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setMessage(e.target.value);
      setTimeout(adjustHeight, 0);
    },
  );

  const handleSubmit = useHandler(async () => {
    if (!message.trim() || isMessageGenerating) {
      return;
    }

    const trimmedMessage = message.trim();

    try {
      if (hasMessages) {
        sendMessage(trimmedMessage);
      } else {
        await createNewChat(trimmedMessage);
      }
      // Clear input before sending to give immediate feedback
      adjustHeight();
      onSendMessage();
    } catch (error) {
      console.error("Error sending message:", error);
      // Restore the message if there was an error
      setMessage(trimmedMessage);
    }
  });

  const handleKeyDown = useHandler(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        handleSubmit();
      }
    },
  );

  const handleCancel = useCallback(() => {
    // Cancel all processing messages
    for (const messageId of cancellableMessageIds) {
      cancelMessage(messageId);
    }
  }, [cancellableMessageIds, cancelMessage]);

  const showMessageValue = useShowMessageValue();

  return (
    <label
      className={cn(
        "px-4 py-[14px] mx-auto flex flex-col gap-2 rounded-xl w-full text-left cursor-text",
        styles.chatInput,
      )}
    >
      <textarea
        ref={textareaRef}
        placeholder={placeholder}
        value={showMessageValue ? message : ""}
        onChange={handleMessageChange}
        onKeyDown={handleKeyDown}
        disabled={isMessageGenerating}
        style={{ all: "unset", textAlign: "left" }}
        className="resize-none overflow-y-auto placeholder:text-muted-foreground focus:outline-none min-h-[24px] leading-6"
        rows={1}
      />
      <div className="flex justify-between items-center">
        <DrawerTabs />
        <div>
          {isMessageGenerating ? (
            <Button
              variant="default"
              size="icon-xs"
              className="bg-border p-4"
              onClick={handleCancel}
              aria-label="Cancel message generation"
            >
              <X className="h-4 w-4" />
            </Button>
          ) : (
            <Button
              variant="default"
              size="icon-xs"
              className="bg-border p-4"
              onClick={handleSubmit}
              disabled={!message.trim()}
              aria-label="Send message"
            >
              <ArrowUp className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>
    </label>
  );
});

function useShowMessageValue() {
  const hasMessages = useMessageStore((state) => state.messages.length > 0);
  const isLoading = useMessageStore((state) => state.isLoading);
  return hasMessages || !isLoading;
}
